import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Button } from "@/components/flexkit/Button";
import { useListGroups, useListRoleAssignments } from "@/queries/group-queries";
import { useOrganization } from "@/queries/organization-queries";
import { useFlagOrgRolesEnabled } from "@/hooks/use-flag";
import { GroupsList } from "@/components/settings/organization/groups/GroupsList";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import type { FC } from "react";

type GroupsTabProps = {
    onCreateGroup?: () => void;
    canCreateGroup?: boolean;
};

export const GroupsTab: FC<GroupsTabProps> = ({ onCreateGroup, canCreateGroup = true }) => {
    const { data: groupsData, isLoading: isLoadingGroups, error } = useListGroups();
    const { data: organization, isPending: isLoadingOrganization } = useOrganization();
    const { value: orgRolesEnabled, loading: flagLoading } = useFlagOrgRolesEnabled();
    const orgId = organization?.id ?? "";
    const groups = groupsData?.groups || [];

    // Start fetching role assignments in parallel with groups — avoids waterfall
    const { data: roleAssignments } = useListRoleAssignments({
        resourceType: ResourceType.ORGANIZATION,
        resourceId: orgId,
        enabled: orgRolesEnabled && !!orgId,
    });

    const isLoading = isLoadingGroups || isLoadingOrganization || flagLoading;

    // Gate on org data and flag — we need both to decide what to render.
    // Without the flag gate, orgRolesEnabled defaults to false while loading,
    // causing GroupCard to mount with DeleteGroupDialog which fires N*2 ListRoleAssignments calls.
    if (isLoadingOrganization || flagLoading) {
        return <LoadingState />;
    }

    const isEnterpriseTier = organization?.tier === OrganizationTier.ENTERPRISE;

    if (error) {
        return (
            <div className="flex items-center justify-center py-12">
                <p className="text-content-secondary">Failed to load groups</p>
            </div>
        );
    }

    if (!isEnterpriseTier) {
        return null;
    }

    // Show empty state only after loading completes and we know there are no groups
    if (!isLoading && groups.length === 0) {
        return (
            <EmptyStateCard
                title="You don't have any groups yet"
                description="Groups allow you to manage access to projects and runners."
                data-testid="groups-empty-state"
            >
                {canCreateGroup && (
                    <Button variant="primary" size="sm" data-tracking-id="new-group" onClick={onCreateGroup}>
                        New Group
                    </Button>
                )}
            </EmptyStateCard>
        );
    }

    return <GroupsList groups={groups} orgRolesEnabled={orgRolesEnabled} isLoading={isLoading} roleAssignments={roleAssignments} />;
};
